/** @jsxImportSource jsx-dom */

import { ReactNode } from "jsx-dom";
import { labelClass } from "./shared";

export type LocaleSelectOption = {
  value: string;
  label: string;
  // Set when the label is in another language, so screen readers pronounce it correctly
  lang?: string;
};

type LocaleSelectProps = {
  id: string;
  label: string;
  // Shown in the cell before the select, e.g. the translate icon or the country flag
  icon: ReactNode;
  options: LocaleSelectOption[];
  selected: string;
  testId: string;
  onChange: (value: string) => void;
};

const wrapperClass =
  "tw:flex tw:items-stretch tw:focus-within:ring-2 tw:focus-within:ring-fruugo-blue-secondary";
// Fixed 44px width (the design's translate cell) so both cells match whatever icon they hold; the Figma flag cell is 40px
const iconCellClass =
  "tw:flex tw:shrink-0 tw:w-11 tw:items-center tw:justify-center tw:bg-fruugo-off-white tw:border tw:border-e-0 tw:border-fruugo-accessible-border-control";
// Native select styling is removed (appearance-none) so its rounded corners and background don't show,
// and the design's caret is drawn instead. Logical (start/end) properties keep it correct in RTL.
// A 24px line box leaves room for descenders (leading-none clipped the "g" in Safari); with the 8.5px padding and 1px
// border it comes to the design's 43px while still growing with the text.
const selectClass =
  "tw:block tw:w-full tw:appearance-none tw:rounded-none tw:border tw:border-fruugo-accessible-border-control tw:bg-white tw:py-[8.5px] tw:leading-6 tw:ps-4 tw:pe-10 tw:text-base tw:text-fruugo-black tw:focus:outline-none";
const caretClass =
  "tw:pointer-events-none tw:absolute tw:end-4 tw:top-1/2 tw:-translate-y-1/2";

export const LocaleSelect = ({
  id,
  label,
  icon,
  options,
  selected,
  testId,
  onChange,
}: LocaleSelectProps) => (
  <div>
    <label htmlFor={id} class={labelClass}>
      {label}
    </label>
    <div class={wrapperClass}>
      <span class={iconCellClass} aria-hidden="true">
        {icon}
      </span>
      <div class="tw:relative tw:flex-1 tw:min-w-0">
        <select
          id={id}
          class={selectClass}
          data-testid={testId}
          onChange={(event: Event) =>
            onChange((event.target as HTMLSelectElement).value)
          }
        >
          {options.map((option) => (
            <option
              value={option.value}
              lang={option.lang}
              selected={option.value === selected}
            >
              {option.label}
            </option>
          ))}
        </select>
        <img
          src="/marketplace/images/select-caret.svg"
          width="9"
          height="10"
          alt=""
          class={caretClass}
        />
      </div>
    </div>
  </div>
);
